import { toLegacyDetail, toLegacyPaginated, v1Delete, v1Get, v1Post, v1Put } from '@/services/v1Api';

/**
 * Get all staff members for a company
 * @param {number|string} companyId - Company ID
 * @param {Object} params - Query parameters (active, primary_only, search, per_page, page)
 * @returns {Promise} API response
 */
export const getCompanyStaff = async (companyId, params = {}) => {
    try {
        const query = {};

        if (params.active !== undefined && params.active !== null) {
            query.active = params.active;
        }
        if (params.primary_only !== undefined && params.primary_only !== null) {
            query.primary_only = params.primary_only;
        }
        if (params.search) query.search = params.search;
        if (params.per_page) query.per_page = params.per_page;
        if (params.page) query.page = params.page;

        const { data, meta } = await v1Get(`/api/v1/address-book/companies/${companyId}/staff`, query);
        return toLegacyPaginated(data, meta);
    } catch (error) {
        console.error('Error fetching company staff:', error);
        throw error;
    }
};

/**
 * Get a single staff member by ID
 * @param {number|string} companyId - Company ID
 * @param {number|string} staffId - Staff ID
 * @returns {Promise} API response
 */
export const getStaffMemberById = async (companyId, staffId) => {
    try {
        const { data } = await v1Get(`/api/v1/address-book/companies/${companyId}/staff/${staffId}`);
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error fetching staff member by ID:', error);
        throw error;
    }
};

/**
 * Add a staff member to a company
 * @param {number|string} companyId - Company ID
 * @param {Object} staffData - Staff data (contact_id, position, department, is_primary_contact, notes, start_date, end_date, is_active)
 * @returns {Promise} API response
 */
export const addStaffMember = async (companyId, staffData) => {
    try {
        const { data } = await v1Post(`/api/v1/address-book/companies/${companyId}/staff`, staffData);
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error adding staff member:', error);
        throw error;
    }
};

/**
 * Update a staff member
 * @param {number|string} companyId - Company ID
 * @param {number|string} staffId - Staff ID
 * @param {Object} staffData - Staff data (all fields optional)
 * @returns {Promise} API response
 */
export const updateStaffMember = async (companyId, staffId, staffData) => {
    try {
        const { data } = await v1Put(`/api/v1/address-book/companies/${companyId}/staff/${staffId}`, staffData);
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error updating staff member:', error);
        throw error;
    }
};

/**
 * Delete a staff member (soft delete)
 * @param {number|string} companyId - Company ID
 * @param {number|string} staffId - Staff ID
 * @returns {Promise} API response
 */
export const deleteStaffMember = async (companyId, staffId) => {
    try {
        await v1Delete(`/api/v1/address-book/companies/${companyId}/staff/${staffId}`);
        return { success: true };
    } catch (error) {
        console.error('Error deleting staff member:', error);
        throw error;
    }
};

/**
 * Bulk add staff members to a company
 * @param {number|string} companyId - Company ID
 * @param {Array} contacts - Array of contact objects with staff data
 * @returns {Promise} API response
 */
export const bulkAddStaffMembers = async (companyId, contacts) => {
    try {
        const { data } = await v1Post(`/api/v1/address-book/companies/${companyId}/staff/bulk`, { contacts });
        return toLegacyDetail(data);
    } catch (error) {
        console.error('Error bulk adding staff members:', error);
        throw error;
    }
};
